import React, { useState, useEffect, useRef } from 'react';
import './Messaging.scss';
import withAuth from '../../auth/WithAuth';
import FurnitureNavbar from '../../../components/layout/Nav';
import { serverUrlAuth } from '../../../serverUrl';
import { toast } from 'react-toastify';
import {
  FaSearch,
  FaUserCircle,
  FaComments,
  FaPaperPlane,
  FaArrowLeft,
  FaCheckDouble,
  FaClock,
  FaCheck,
  FaHandshake,
  FaMoneyBillWave,
  FaCheckCircle,
  FaFlag,
} from 'react-icons/fa';
import { useLocation, useNavigate } from 'react-router-dom';
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';

const Messages = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);
  const preselectedProposalId = queryParams.get('proposal');

  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showSidebar, setShowSidebar] = useState(true);
  const [negotiationDetails, setNegotiationDetails] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [acceptingOffer, setAcceptingOffer] = useState(false);
  const [projectStatus, setProjectStatus] = useState(null);
  const [isArtisanSelected, setIsArtisanSelected] = useState(false);
  const [currentProjectId, setCurrentProjectId] = useState(null);
  const [pendingOfferAmount, setPendingOfferAmount] = useState(null);

  // Modal states
  const [showFinalAgreementModal, setShowFinalAgreementModal] = useState(false);
  const [finalAgreementAmount, setFinalAgreementAmount] = useState('');
  const [isSubmittingFinalAgreement, setIsSubmittingFinalAgreement] = useState(false);

  const messagesEndRef = useRef(null);
  const messageIntervalRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const isPollingRef = useRef(false);

  // Mark all messages as read when opening a conversation
  const markMessagesAsRead = async (projectId) => {
    if (!projectId) return;
    
    try {
      console.log('Marking messages as read for project:', projectId);
      const response = await serverUrlAuth.post(`/updates/mark-messages-as-read/${projectId}/mark-all-read/`);
      console.log('Mark as read response:', response.data);
      
      // Update conversation list to reflect read status
      const convResponse = await serverUrlAuth.get('negotiations/inbox/');
      setConversations(convResponse.data);
      
    } catch (err) {
      console.error('Error marking messages as read:', err);
    }
  };

  // Fetch user role from the API
  const fetchUserRole = async () => {
    try {
      const response = await serverUrlAuth.get('users/profile/');
      console.log('Profile response:', response.data);
      const role = response.data.role || response.data.user_role || response.data.user_type;
      setUserRole(role);
      return role;
    } catch (err) {
      console.error('Error fetching user role:', err);
      return null;
    }
  };

  // Fetch project status
  const fetchProjectStatus = async (projectId) => {
    if (!projectId) return;
    try {
      const response = await serverUrlAuth.get(`projects/client-project/${projectId}/`);
      setProjectStatus(response.data.status);
      const isSelected = response.data.status === 'in-progress' || response.data.status === 'completed';
      setIsArtisanSelected(isSelected);
    } catch (err) {
      console.error('Error fetching project status:', err);
    }
  };

  // Fetch all conversations (negotiations)
  const fetchConversations = async () => {
    try {
      const response = await serverUrlAuth.get('negotiations/inbox/');
      console.log('Conversations:', response.data);
      setConversations(response.data);

      if (!userRole && response.data.length > 0) {
        setUserRole('client');
      }

      if (preselectedProposalId && !selectedConversation) {
        let conversation = response.data.find(
          (c) => c.proposal?.toString() === preselectedProposalId,
        );

        if (conversation) {
          setSelectedConversation(conversation);
          setCurrentProjectId(conversation.proposal_details?.project);
          setShowSidebar(false);
          await fetchNegotiationDetails(conversation.proposal);
          await fetchMessages(conversation.proposal_details?.project);
          await markMessagesAsRead(conversation.proposal_details?.project);
        } else {
          await fetchSingleConversation(preselectedProposalId);
        }
      }
    } catch (err) {
      console.error('Error fetching conversations:', err);
      toast.error('Failed to load conversations');
    } finally {
      setLoading(false);
    }
  };

  // Fetch a single conversation by proposal ID
  const fetchSingleConversation = async (proposalId) => {
    try {
      const response = await serverUrlAuth.get(`negotiations/proposal/${proposalId}/`);
      const convData = response.data;
      console.log('Single conversation:', convData);

      // Fetch project budget
      let projectBudget = convData.proposal_details?.proposed_price || 0;
      try {
        const projectRes = await serverUrlAuth.get(`projects/client-project/${convData.proposal_details?.project}/`);
        projectBudget = projectRes.data.budget;
      } catch (err) {
        console.error('Error fetching project budget:', err);
      }

      const newConversation = {
        id: convData.id,
        proposal: proposalId,
        project_id: convData.proposal_details?.project,
        project_title: convData.project_title,
        artisan_name: convData.artisan_name,
        client_name: convData.client_name,
        last_message_time: convData.created_at,
        unread_count: 0,
        budget: projectBudget,
        proposed_price: convData.proposal_details?.proposed_price,
      };

      setCurrentProjectId(newConversation.project_id);
      setSelectedConversation(newConversation);
      setShowSidebar(false);
      setNegotiationDetails(convData);
      
      // Check for pending offer
      if (convData.pending_final_amount) {
        setPendingOfferAmount(convData.pending_final_amount);
      }
      
      await fetchProjectStatus(newConversation.project_id);
      await fetchMessages(newConversation.project_id);
      await markMessagesAsRead(newConversation.project_id);
    } catch (err) {
      console.error('Error fetching single conversation:', err);
      toast.error('Could not open conversation');
    }
  };

  // Fetch messages from COMBINED endpoint
  const fetchMessages = async (projectId, isPolling = false) => {
    if (!projectId) {
      console.log('No projectId, skipping fetch');
      return;
    }

    if (!isPolling) {
      setLoadingMessages(true);
    }

    try {
      console.log('Fetching messages for project:', projectId);
      const response = await serverUrlAuth.get(`/updates/messages/${projectId}/`);
      console.log('Messages received:', response.data);

      const processedMessages = response.data.map((msg) => ({
        ...msg,
        id: msg.id,
        message: msg.message || msg.content,
        timestamp: msg.created_at || msg.timestamp,
        sender_role: msg.sender_role,
        read_by_client: msg.read_by_client,
        read_by_artisan: msg.read_by_artisan,
        type: msg.type || 'chat',
        source: msg.source,
      }));

      setMessages(processedMessages);
      scrollToBottom();
    } catch (err) {
      console.error('Error fetching messages:', err);
      if (!isPolling) {
        toast.error('Failed to load messages');
      }
    } finally {
      if (!isPolling) {
        setLoadingMessages(false);
      }
    }
  };

  // Fetch negotiation details
  const fetchNegotiationDetails = async (proposalId) => {
    try {
      const response = await serverUrlAuth.get(`negotiations/proposal/${proposalId}/`);
      console.log('Negotiation details:', response.data);
      setNegotiationDetails(response.data);
      
      // Update pending offer amount
      if (response.data.pending_final_amount) {
        setPendingOfferAmount(response.data.pending_final_amount);
      } else {
        setPendingOfferAmount(null);
      }
      
      if (response.data.proposal_details?.project) {
        setCurrentProjectId(response.data.proposal_details?.project);
        await fetchProjectStatus(response.data.proposal_details?.project);
      }
    } catch (err) {
      console.error('Error fetching negotiation details:', err);
    }
  };

  // Send message
  const handleSendMessage = async () => {
    if (!newMessage.trim() || !selectedConversation || isSending) return;

    setIsSending(true);

    const tempMessage = {
      id: Date.now(),
      message: newMessage.trim(),
      sender_role: userRole,
      timestamp: new Date().toISOString(),
      temp_sent: true,
      read_by_client: false,
      read_by_artisan: false,
    };

    setMessages((prev) => [...prev, tempMessage]);
    setNewMessage('');
    scrollToBottom();

    try {
      let response;

      if (isArtisanSelected && currentProjectId) {
        const formData = new FormData();
        formData.append('type', 'chat');
        formData.append('message', newMessage.trim());
        response = await serverUrlAuth.post(`/updates/post-updates/${currentProjectId}/`, formData);
      } else {
        response = await serverUrlAuth.post(
          `negotiations/proposal/${selectedConversation.proposal}/messages/`,
          { message: newMessage.trim() }
        );
      }

      setMessages((prev) =>
        prev.map((msg) => (msg.id === tempMessage.id ? response.data : msg))
      );

      const convResponse = await serverUrlAuth.get('negotiations/inbox/');
      setConversations(convResponse.data);
      
      // Refresh negotiation details to get updated pending_final_amount
      await fetchNegotiationDetails(selectedConversation.proposal);
    } catch (err) {
      setMessages((prev) => prev.filter((msg) => msg.id !== tempMessage.id));
      console.error('Failed to send message:', err);
      if (err.response?.data?.detail === 'Your message contains restricted content and cannot be delivered.') {
        toast.error('⚠️ Message blocked: Contains restricted content (phone numbers, emails)');
      } else {
        toast.error('Failed to send message.');
      }
    } finally {
      setIsSending(false);
    }
  };

  // ARTISAN: Submit final agreement
  const handleSubmitFinalAgreement = async () => {
    if (!finalAgreementAmount || !selectedConversation || !negotiationDetails) return;

    setIsSubmittingFinalAgreement(true);
    try {
      await serverUrlAuth.patch(`negotiations/submit-final-agreement/${negotiationDetails.id}/`, {
        final_agreed_amount: finalAgreementAmount,
      });

      toast.success(`Final agreement submitted! Waiting for client acceptance.`);
      setShowFinalAgreementModal(false);
      setFinalAgreementAmount('');
      await fetchNegotiationDetails(selectedConversation.proposal);
      await fetchMessages(currentProjectId);
    } catch (err) {
      console.error('Error submitting final agreement:', err);
      toast.error('Failed to submit final agreement');
    } finally {
      setIsSubmittingFinalAgreement(false);
    }
  };

  // CLIENT: Accept final agreement
  const handleAcceptFinalAgreement = async () => {
    if (!negotiationDetails?.pending_final_amount) return;

    setAcceptingOffer(true);
    try {
      await serverUrlAuth.patch(`negotiations/accept-final-agreement/${negotiationDetails.id}/`);

      toast.success('✅ Price agreed! Redirecting to your project to select the artisan...');

      sessionStorage.setItem(
        'openProjectMatches',
        negotiationDetails.proposal_details?.project?.toString() || selectedConversation?.project,
      );
      sessionStorage.setItem(
        'selectedArtisanId',
        negotiationDetails.proposal_details?.artisan?.toString(),
      );
      sessionStorage.setItem('showPriceAgreedMessage', 'true');

      navigate('/projects');
    } catch (err) {
      console.error('Error accepting agreement:', err);
      toast.error('Failed to accept agreement. Please try again.');
    } finally {
      setAcceptingOffer(false);
    }
  };

  // Select conversation
  const handleSelectConversation = async (conversation) => {
    setSelectedConversation(conversation);
    setCurrentProjectId(conversation.proposal_details?.project);
    await fetchNegotiationDetails(conversation.proposal);
    await fetchMessages(conversation.proposal_details?.project);
    await markMessagesAsRead(conversation.proposal_details?.project);
    setShowSidebar(false);
  };

  const scrollToBottom = () => {
    setTimeout(() => {
      if (messagesContainerRef.current) {
        messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
      }
    }, 100);
  };

  const startPolling = () => {
    if (messageIntervalRef.current) clearInterval(messageIntervalRef.current);
    
    messageIntervalRef.current = setInterval(async () => {
      if (selectedConversation && currentProjectId && !isPollingRef.current) {
        isPollingRef.current = true;
        try {
          const currentMessageCount = messages.length;
          await fetchMessages(currentProjectId, true);
          
          // Refresh negotiation details to check for new pending offers
          await fetchNegotiationDetails(selectedConversation.proposal);
          
          if (messages.length > currentMessageCount) {
            scrollToBottom();
          }
        } catch (error) {
          console.error('Polling error:', error);
        } finally {
          isPollingRef.current = false;
        }
      }
    }, 5000);
  };

  const stopPolling = () => {
    if (messageIntervalRef.current) {
      clearInterval(messageIntervalRef.current);
      messageIntervalRef.current = null;
    }
  };

  const filteredConversations = conversations.filter(
    (conv) =>
      conv.artisan_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      conv.client_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      conv.project_title?.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now - date;
    const hours = Math.floor(diff / (1000 * 60 * 60));

    if (hours < 1) {
      const mins = Math.floor(diff / (1000 * 60));
      return mins === 0 ? 'Just now' : `${mins} min ago`;
    } else if (hours < 24) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (hours < 48) {
      return 'Yesterday';
    } else {
      return date.toLocaleDateString();
    }
  };

  const formatMessageTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const getUnreadCount = (conversation) => conversation.unread_count || 0;

  const getDisplayName = (conversation) => {
    if (effectiveUserRole === 'client') {
      return conversation.artisan_name || conversation.other_party_name || 'Artisan';
    } else if (effectiveUserRole === 'artisan') {
      return conversation.client_name || conversation.other_party_name || 'Client';
    }
    return conversation.artisan_name || conversation.client_name || 'User';
  };

  useEffect(() => {
    fetchUserRole();
    fetchConversations();
    return () => stopPolling();
  }, []);

  useEffect(() => {
    if (selectedConversation && currentProjectId) {
      startPolling();
    } else {
      stopPolling();
    }
    return () => stopPolling();
  }, [selectedConversation, currentProjectId]);

  const effectiveUserRole = userRole || 'client';
  const effectiveIsClient = effectiveUserRole === 'client';

  const isChatDisabled = negotiationDetails?.is_closed === true;
  const shouldShowMatchesButton = !isArtisanSelected && negotiationDetails?.final_agreed_amount;
  
  // Check if there's a pending offer that hasn't been accepted yet
  const hasPendingOffer = negotiationDetails?.pending_final_amount && !negotiationDetails?.final_agreed_amount;

  console.log('Client Messages - Current state:', {
    currentProjectId,
    isArtisanSelected,
    messagesCount: messages.length,
    negotiationDetails,
    hasPendingOffer,
    pendingOfferAmount: negotiationDetails?.pending_final_amount,
  });

  return (
    <>
      <FurnitureNavbar />
      <div className="messages-page">
        <div className="whatsapp-container">
          {/* Sidebar */}
          <div className={`conversations-sidebar ${showSidebar ? 'open' : 'closed'}`}>
            <div className="sidebar-header">
              <div className="header-top">
                <h2>Chats</h2>
              </div>
              <div className="search-bar">
                <FaSearch className="search-icon" />
                <input
                  type="text"
                  placeholder="Search or start new chat"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
            <div className="conversations-list">
              {loading ? (
                <div className="loading-state">
                  <div className="spinner"></div>
                  <p>Loading chats...</p>
                </div>
              ) : filteredConversations.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-icon">💬</div>
                  <h4>No chats yet</h4>
                  <p>When you start a conversation, it will appear here.</p>
                </div>
              ) : (
                filteredConversations.map((conv) => {
                  const unreadCount = getUnreadCount(conv);
                  return (
                    <div
                      key={conv.id}
                      className={`conversation-item ${selectedConversation?.id === conv.id ? 'active' : ''}`}
                      onClick={() => handleSelectConversation(conv)}
                    >
                      <div className="avatar">
                        <FaUserCircle />
                      </div>
                      <div className="conversation-info">
                        <div className="conversation-header">
                          <h4>{getDisplayName(conv)}</h4>
                          <span className="timestamp">{formatTime(conv.last_message_time)}</span>
                        </div>
                        <div className="conversation-preview">
                          <p className="project-title">{conv.project_title}</p>
                        </div>
                      </div>
                      {unreadCount > 0 && <div className="unread-badge">{unreadCount}</div>}
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* Main Chat Area */}
          <div className="chat-area">
            {!selectedConversation ? (
              <div className="no-conversation-selected">
                <div className="no-chat-icon">
                  <FaComments />
                </div>
                <h3>Select a conversation</h3>
                <p>Choose a chat from the list to start messaging</p>
                <button className="back-to-chats-btn" onClick={() => setShowSidebar(true)}>
                  Back to Chats
                </button>
              </div>
            ) : (
              <>
                <div className="chat-header">
                  <button
                    className="back-button"
                    onClick={() => {
                      setShowSidebar(true);
                      setSelectedConversation(null);
                    }}
                  >
                    <FaArrowLeft />
                  </button>
                  <div className="chat-user-info">
                    <div className="user-avatar">
                      <FaUserCircle />
                    </div>
                    <div className="user-details">
                      <h3>{getDisplayName(selectedConversation)}</h3>
                      <p className="project-context">
                        Project: {selectedConversation.project_title}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Status Banner */}
                {isArtisanSelected ? (
                  <div className="project-status-banner">
                    <div className="status-info">
                      <FaCheck className="status-icon" />
                      <span>Project In Progress</span>
                    </div>
                    <div className="info-note">💬 Chat with artisan for project updates</div>
                  </div>
                ) : (
                  negotiationDetails && (
                    <div className="price-status-banner">
                      {negotiationDetails.final_agreed_amount ? (
                        <div className="price-accepted">
                          <FaHandshake /> Price Agreed: KSh{' '}
                          {Number(negotiationDetails.final_agreed_amount).toLocaleString()}
                          <div className="action-needed">
                            <span>✓ Price agreed! You can continue chatting.</span>
                            {shouldShowMatchesButton && (
                              <button
                                className="select-artisan-btn"
                                onClick={() => {
                                  sessionStorage.setItem(
                                    'openProjectMatches',
                                    negotiationDetails.proposal_details?.project?.toString() ||
                                      selectedConversation?.project,
                                  );
                                  navigate('/projects');
                                }}
                              >
                                Go to Matches To Select Artisan
                              </button>
                            )}
                          </div>
                        </div>
                      ) : negotiationDetails.pending_final_amount ? (
                        <div className="price-pending">
                          <FaMoneyBillWave /> Pending Offer: KSh{' '}
                          {Number(negotiationDetails.pending_final_amount).toLocaleString()}
                          
                          {!effectiveIsClient && (
                            <span className="waiting-text">Waiting for client to accept price...</span>
                          )}
                        </div>
                      ) : negotiationDetails.is_closed ? (
                        <div className="price-completed">
                          <FaCheckCircle /> Project Completed! Thank you for working together.
                        </div>
                      ) : (
                        <div className="price-negotiating">
                          💬 Negotiating - Discuss the price with the artisan
                        </div>
                      )}
                    </div>
                  )
                )}

                {/* Messages Area */}
                <div className="messages-area" ref={messagesContainerRef}>
                  {loadingMessages && messages.length === 0 ? (
                    <div className="loading-messages">
                      <div className="spinner"></div>
                      <p>Loading messages...</p>
                    </div>
                  ) : messages.length === 0 ? (
                    <div className="no-messages">
                      <div className="no-messages-icon">💬</div>
                      <p>No messages yet</p>
                      <small>Send a message to start the conversation</small>
                    </div>
                  ) : (
                    messages.map((msg, index) => {
                      const isSent = effectiveIsClient
                        ? msg.sender_role === 'client'
                        : msg.sender_role === 'artisan';
                      const isMilestone = msg.type === 'milestone';
                      
                      // Check if this message contains a final agreement submission
                      const isFinalAgreementMessage = msg.message && 
                        msg.message.includes('Final agreement submitted') && 
                        msg.offer;
                      
                      const showDate =
                        index === 0 ||
                        (messages[index - 1] &&
                          new Date(msg.timestamp || msg.created_at).toDateString() !==
                            new Date(messages[index - 1].timestamp || messages[index - 1].created_at).toDateString());

                      return (
                        <React.Fragment key={msg.id}>
                          {showDate && (
                            <div className="date-divider">
                              <span>
                                {new Date(msg.timestamp || msg.created_at).toLocaleDateString(undefined, {
                                  weekday: 'long',
                                  year: 'numeric',
                                  month: 'long',
                                  day: 'numeric',
                                })}
                              </span>
                            </div>
                          )}
                          <div className={`message-bubble ${isSent ? 'sent' : 'received'}`}>
                            <div className="message-content">
                              {isMilestone ? (
                                <div className="milestone-update">
                                  <div className="milestone-header">
                                    <FaFlag className="milestone-icon" />
                                    <span className="milestone-label">Milestone Update</span>
                                  </div>
                                  <div className="progress-section">
                                    <div className="progress-info">
                                      <span>Progress: {msg.progress_percentage}%</span>
                                    </div>
                                    <div className="progress-bar-container">
                                      <div
                                        className="progress-fill"
                                        style={{ width: `${msg.progress_percentage}%` }}
                                      />
                                    </div>
                                  </div>
                                  <div className="milestone-note">{msg.note}</div>
                                </div>
                              ) : (
                                <>
                                  <p>{msg.message}</p>
                                  {/* Show Accept Offer button for final agreement messages from artisan to client */}
                                  {isFinalAgreementMessage && effectiveIsClient && !negotiationDetails?.final_agreed_amount && (
                                    <div className="offer-accept-container">
                                      <div className="offer-details">
                                        <strong>💰 Final Offer: KSh {Number(msg.offer).toLocaleString()}</strong>
                                      </div>
                                      <button
                                        className="accept-offer-btn"
                                        onClick={handleAcceptFinalAgreement}
                                        disabled={acceptingOffer}
                                      >
                                        {acceptingOffer ? (
                                          <>Processing...</>
                                        ) : (
                                          <>✅ Accept This Offer</>
                                        )}
                                      </button>
                                    </div>
                                  )}
                                </>
                              )}
                              {msg.offer && !isFinalAgreementMessage && (
                                <div className={`offer-bubble ${msg.is_accepted ? 'accepted' : ''}`}>
                                  {msg.is_accepted ? (
                                    <>
                                      <FaCheckCircle /> Accepted Offer: KSh {Number(msg.offer).toLocaleString()}
                                    </>
                                  ) : (
                                    <>
                                      💰 Price Proposal: KSh {Number(msg.offer).toLocaleString()}
                                    </>
                                  )}
                                </div>
                              )}
                              {msg.images && msg.images.length > 0 && (
                                <div className="message-images">
                                  {msg.images.map((img, imgIndex) => (
                                    <img
                                      key={imgIndex}
                                      src={img.image_url || img.image}
                                      alt="Message attachment"
                                      className="message-image"
                                    />
                                  ))}
                                </div>
                              )}
                            </div>
                            <div className="message-meta">
                              <span className="message-time">
                                {formatMessageTime(msg.timestamp || msg.created_at)}
                              </span>
                              {isSent && (
                                <span className="read-status">
                                  {msg.read_by_client && msg.read_by_artisan ? (
                                    <FaCheckDouble />
                                  ) : (
                                    <FaCheck />
                                  )}
                                </span>
                              )}
                            </div>
                          </div>
                        </React.Fragment>
                      );
                    })
                  )}
                  <div ref={messagesEndRef} />
                </div>

                {/* Chat Input Area */}
                <div className="message-input-area">
                  <div className="input-wrapper">
                    <input
                      type="text"
                      placeholder={isChatDisabled ? 'Chat is closed - Project completed' : 'Type a message...'}
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey && !isChatDisabled) {
                          e.preventDefault();
                          handleSendMessage();
                        }
                      }}
                      className="message-input"
                      disabled={isChatDisabled}
                    />
                    <button
                      onClick={handleSendMessage}
                      disabled={!newMessage.trim() || isSending || isChatDisabled}
                      className="send-button"
                    >
                      {isSending ? <FaClock /> : <FaPaperPlane />}
                    </button>
                  </div>
                  {!effectiveIsClient && !isChatDisabled && !negotiationDetails?.final_agreed_amount && (
                    <div className="final-agreement-button-container">
                      <button
                        className="final-agreement-btn"
                        onClick={() => setShowFinalAgreementModal(true)}
                      >
                        <FaHandshake /> Submit Final Agreement
                      </button>
                    </div>
                  )}
                  {isChatDisabled && (
                    <div className="chat-disabled-note">
                      <small>This project is completed. Chat is closed.</small>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Artisan: Submit Final Agreement Modal */}
      <Modal
        show={showFinalAgreementModal}
        onHide={() => {
          setShowFinalAgreementModal(false);
          setFinalAgreementAmount('');
        }}
        centered
        className="price-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>Submit Final Agreement</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="price-modal-content">
            <p className="info-text">
              Submit the final agreed price for this project. The client will need to accept it to confirm the project.
            </p>
            <div className="price-input-group">
              <label>Final Agreed Amount (KSh)</label>
              <input
                type="number"
                className="price-input"
                placeholder="Enter final agreed amount"
                value={finalAgreementAmount}
                onChange={(e) => setFinalAgreementAmount(e.target.value)}
              />
            </div>
            {selectedConversation && (
              <>
                <div className="project-budget">
                  <strong>Original Project Budget:</strong> KSh{' '}
                  {Number(selectedConversation.budget || 0).toLocaleString()}
                </div>
                <div className="project-budget">
                  <strong>Artisan's Proposed Price:</strong> KSh{' '}
                  {Number(selectedConversation.proposed_price || 0).toLocaleString()}
                </div>
              </>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowFinalAgreementModal(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmitFinalAgreement}
            disabled={!finalAgreementAmount || isSubmittingFinalAgreement}
          >
            {isSubmittingFinalAgreement ? 'Submitting...' : 'Submit Agreement'}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default withAuth(Messages, 'client');